import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Stack, Tab } from "@mui/material";
import { HubError } from "../../class/Error";
import { useParams } from "react-router-dom";
import { TabContext } from "@mui/lab";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { showNotification } from "../../store/slices/notificationSlice";
import { RootState } from "../../store/store";
import Box from "@mui/material/Box";
import { useInfiniteLoad } from "../../_hooks/useInfiniteLoad";
import useLocalStorage from "../../_hooks/useLocalStorage";
import { TabPanelStyled } from "../../_components/TabPanelStyled";
import { TabsStyled } from "../../_components/TabsStyled";
import { Strategy } from "../../store/slices/strategySlice";
import BotFilters from "../../_components/BotFilters/BotFilters";
import BottomButton from "../../_components/BottomButton";
import AutoStrategyList from "./components/AutoStrategyList";
import Header from "../../_modules/Header/Header";
import PageLayout from "../../_components/PageLayout";
import { useApi } from "../../_hooks/use-api";
import { useAppNavigate } from "../../_hooks/use-app-navigate";
import CardsListSkeleton from "../../_components/CardsListSkeleton/CardsListSkeleton";
import { useTranslation } from "react-i18next";
import { myT } from "../../_i18n/utils";

const getActiveSortOptions = () => {
  return [
    {
      value: "startTime:asc",
      label: myT("creationTime", "botsList"),
      subLabel: myT("increase", "botsList"),
      sortType: "asc",
      sortIcon: "SortTimeAsc",
    },
    {
      value: "startTime:desc",
      label: myT("creationTime", "botsList"),
      subLabel: myT("decrease", "botsList"),
      sortType: "desc",
      sortIcon: "SortTimeDesc",
    },
    {
      value: "investmentQuote:asc",
      label: myT("investment", "botsList"),
      subLabel: myT("increase", "botsList"),
      sortType: "asc",
      sortIcon: "SortInvestmentAsc",
    },
    {
      value: "investmentQuote:desc",
      label: myT("investment", "botsList"),
      subLabel: myT("decrease", "botsList"),
      sortType: "desc",
      sortIcon: "SortInvestmentDesc",
    },
  ];
};

const getArchivedSortOptions = () => {
  return [
    {
      value: "startTime:asc",
      label: myT("creationTime", "botsList"),
      subLabel: myT("increase", "botsList"),
      sortType: "asc",
      sortIcon: "SortTimeAsc",
    },
    {
      value: "startTime:desc",
      label: myT("creationTime", "botsList"),
      subLabel: myT("decrease", "botsList"),
      sortType: "desc",
      sortIcon: "SortTimeDesc",
    },
    {
      value: "investmentQuote:asc",
      label: myT("investment", "botsList"),
      subLabel: myT("increase", "botsList"),
      sortType: "asc",
      sortIcon: "SortInvestmentAsc",
    },
    {
      value: "investmentQuote:desc",
      label: myT("investment", "botsList"),
      subLabel: myT("decrease", "botsList"),
      sortType: "desc",
      sortIcon: "SortInvestmentDesc",
    },
    {
      value: "closeTime:asc",
      label: myT("closeTime", "botsList"),
      subLabel: myT("increase", "botsList"),
      sortType: "asc",
      sortIcon: "SortTimeAsc",
    },
    {
      value: "closeTime:desc",
      label: myT("closeTime", "botsList"),
      subLabel: myT("decrease", "botsList"),
      sortType: "desc",
      sortIcon: "SortTimeDesc",
    },
  ];
};

interface AutoStrategySortType {
  active: string;
  archived: string;
}

type AutoStrategyType = keyof AutoStrategySortType;

const AutoStrategyListPage = () => {
  const theme = useTheme();
  const navigate = useAppNavigate();
  const api = useApi();
  const { t } = useTranslation("botsList");

  const user = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();

  const autoStrategyType = useParams<{ type: string }>().type as AutoStrategyType;
  if (!autoStrategyType) {
    throw new Error("BotsType is required");
  }

  const [searchValue, setSearchValue] = useState<string>("");

  const [autoStrategySortType, setAutoStrategySortType] = useLocalStorage<AutoStrategySortType>(
    "auto-strategy-sort-type",
    {
      active: getActiveSortOptions()[1].value,
      archived: getArchivedSortOptions()[3].value,
    }
  );

  const fetchAutoStrategy = useCallback(
    async (page: number): Promise<Strategy[]> => {
      try {
        const response = await api.getStrategies(
          user.activeAccount.id,
          user.profile.profileId,
          autoStrategyType.toUpperCase(),
          page,
          autoStrategySortType[autoStrategyType]
        );

        return response.strategies || [];
      } catch (error: any) {
        error instanceof HubError
          ? dispatch(
              showNotification({
                message: `${error.message} ${error.detailedMessage}`,
                severity: "error",
              })
            )
          : dispatch(showNotification({ message: error.message, severity: "error" }));
        throw new Error();
      }
    },
    [user.activeAccount.id, autoStrategyType, autoStrategySortType]
  );

  const { data, isInitialLoading, observerRef, reset } = useInfiniteLoad({
    fetchData: fetchAutoStrategy,
    pageSize: 10,
    initialPage: 0,
  });

  const filteredAutoStrategy = useMemo(() => {
    const lowercasedSearch = searchValue.toLowerCase();
    return data.filter(
      (bot) =>
        bot.base.toLowerCase().includes(lowercasedSearch) ||
        bot.quote.toLowerCase().includes(lowercasedSearch) ||
        (bot.base + bot.quote).toLowerCase().includes(lowercasedSearch)
    );
  }, [data, searchValue]);

  const onChangeSort = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (autoStrategySortType) {
      setAutoStrategySortType({
        active: autoStrategySortType.active,
        archived: autoStrategySortType.archived,
        [autoStrategyType]: event.target.value,
      });
    }
  };

  const onChangeTab = () => {
    navigate.strategyList(autoStrategyType === "active" ? "archived" : "active", true);
  };

  useEffect(() => {
    reset();
  }, [user.activeAccount.id, autoStrategySortType[autoStrategyType]]);

  return (
    <PageLayout
      header={<Header pageTitle={t("automaticStrategies")} needBack toMainScreen />}
      bottomButton={
        <BottomButton
          id={"create-bot-button"}
          label={t("createNewStrategy")}
          onClick={() => navigate.createStrategy()}
        />
      }
      contentSx={{ paddingTop: "20px" }}
    >
      <Box
        className="layout"
        sx={{
          backgroundColor: theme.palette.grey[500],
          borderTopLeftRadius: "12px",
          borderTopRightRadius: "12px",
          padding: "8px 8px 0",
        }}
      >
        <TabContext value={autoStrategyType}>
          <Box
            className="layout-fixed-wrapper"
            sx={{
              borderTopLeftRadius: "12px",
              borderTopRightRadius: "12px",
            }}
          >
            <Stack direction={"row"} spacing={1}>
              <TabsStyled
                style={{
                  borderRadius: 12,
                  backgroundColor: theme.palette.grey[400],
                  width: "100%",
                }}
                onChange={onChangeTab}
              >
                <Tab
                  style={{ maxWidth: "100%" }}
                  label={t("active")}
                  value={"active"}
                  className="AdronSecondaryTabs"
                />
                <Tab
                  style={{ maxWidth: "100%" }}
                  label={t("archived")}
                  value={"archived"}
                  className="AdronSecondaryTabs"
                />
              </TabsStyled>
            </Stack>
            <Stack sx={{ marginTop: 1 }}>
              <BotFilters
                searchValue={searchValue}
                onChangeSearch={setSearchValue}
                value={autoStrategySortType[autoStrategyType]}
                onChange={onChangeSort}
                options={
                  autoStrategyType === "active" ? getActiveSortOptions() : getArchivedSortOptions()
                }
              />
            </Stack>
          </Box>

          <Box sx={{ position: "relative" }} className="layout-content-wrapper">
            {isInitialLoading ? (
              <CardsListSkeleton cardBodyHeight="297px" />
            ) : (
              <>
                <TabPanelStyled sx={{ height: "100%" }} noPadding={true} value="active">
                  <AutoStrategyList strategies={filteredAutoStrategy} ref={observerRef} />
                </TabPanelStyled>
                <TabPanelStyled sx={{ height: "100%" }} noPadding={true} value="archived">
                  <AutoStrategyList strategies={filteredAutoStrategy} ref={observerRef} />
                </TabPanelStyled>
              </>
            )}
          </Box>
        </TabContext>
      </Box>
    </PageLayout>
  );
};

export default AutoStrategyListPage;
